<template>
    <b-container fluid>
        <h1 class="text-left mb-4">{{ title }}</h1>
        <b-badge v-show="isLoading" variant="primary">{{
            $t('loading')
        }}</b-badge>
        <b-form
            v-if="!isLoading"
            ref="tableResultsForm"
            class="pb-5"
            v-on:submit.prevent="$_onSubmit"
        >
            <div class="col-md-6">
                <!-- Name -->
                <x-input
                    :id="`name_${tableResult.id}`"
                    :text="$t('columnNameName')"
                    maxlength="50"
                    v-model="tableResult.name"
                    @input="$_onInputName"
                    :state="validName"
                    :invalidMessage="invalidNameErrorMessage"
                    required
                    autofocus
                >
                </x-input>
                <!-- Title -->
                <x-input
                    :id="`title_${tableResult.id}`"
                    :text="$t('columnNameTitle')"
                    maxlength="50"
                    v-model="tableResult.title"
                    required
                >
                </x-input>
                <!-- Description -->
                <label :for="`description_${tableResult.id}`">{{
                    $t('description')
                }}</label>
                <x-textarea
                    :id="`description_${tableResult.id}`"
                    :rows="3"
                    :maxlength="2000"
                    v-model="tableResult.description"
                    required
                >
                </x-textarea>
                <!-- IsPubliclyAccessible -->
                <x-checkbox
                    :id="'publicly-accessable-id'"
                    :name="'publicly-accessable-name'"
                    class="mb-3"
                    :text="$t('tableResults.isPubliclyAccessible')"
                    :value="tableResult.isPubliclyAccessible"
                    @input="$_onIsPubliclyAccessible"
                ></x-checkbox>
                <!-- Tablename -->
                <x-select
                    :id="`tablename_${tableResult.id}`"
                    :value="tableResult.tableName"
                    labelClass="prewrap"
                    :text="$t('tableResults.tableName')"
                    @selected="$_onTableSelected($event)"
                    :options="layerOptions"
                    :simpleMode="true"
                    :disabled="isEditMode"
                    :extendedClass="{ plaintext: isEditMode }"
                    required
                >
                </x-select>
            </div>
            <div v-if="hasInformationAttributes" class="col-xs-12 col-md-12">
                <b-tabs
                    active-nav-item-class="text-primary"
                    :class="{ 'hide-tabs-table-result': !isEditMode }"
                >
                    <label v-if="!isEditMode">
                        {{ $t('labelTabInformationDisplay') }}
                    </label>
                    <!--Informatieweergave-->
                    <b-tab :title="$t('labelTabInformationDisplay')" active>
                        <information-tab
                            :themelayer="resultLayer"
                            :saving="isSaving"
                            :class="{ 'mt-2': isEditMode }"
                            @update="$_updateInformationAttributes($event)"
                        >
                        </information-tab>
                    </b-tab>
                    <!--Zoekattributen-->
                    <b-tab
                        v-if="isEditMode"
                        :title="$t('tableResults.searchAttributes.title')"
                    >
                        <search-attributes
                            class="mt-3 pb-3"
                            ref="searchAttributesEdit"
                            :informationAttributes="
                                resultLayer.informationAttributes
                            "
                            :layerId="tableResult.id"
                            :layerName="tableResult.tableName"
                        ></search-attributes>
                    </b-tab>
                    <!-- Table relations -->
                    <b-tab
                        v-if="isEditMode"
                        :title="$t('tableResults.relations')"
                    >
                        <x-checkbox
                            name="chkAutoOpen"
                            :text="$t('relations.autoOpenTabs')"
                            v-model="tableResult.autoOpenAllRelationsTabs"
                            class="m-3"
                        >
                        </x-checkbox>
                        <table-relations
                            v-if="tableResult.id > 0"
                            ref="relationsTableEdit"
                            title=""
                            :id="1"
                            :targetLabel="$t('tableResults.titleSingular')"
                            :relationshipType="RELATIONSHIP_TYPES.Table2Table"
                            :layerId="tableResult.id"
                        >
                        </table-relations>
                    </b-tab>
                </b-tabs>
            </div>
            <SubmitCancelRemove
                :is-saving="isSaving"
                :is-deleting="isDeleting"
                :mode="mode"
                :isBtnSaveDisabled="isBtnSaveDisabled"
                @cancel="$_onCancel"
                @remove="$_onRemove"
            >
            </SubmitCancelRemove>
        </b-form>
    </b-container>
</template>

<script>
    import {
        convertToValueTextPair,
        capitalizeValue,
        numberSortFunction,
        valueToName,
        onlyInLeftArray
    } from '../../services/utilities';
    import { MODE_ADD, MODE_EDIT, RELATIONSHIP_TYPES } from '../../constants';
    import modalMixin from '../../components/Mixins/ModalMixin.vue';
    import XInput from '../../components/BaseComponents/x-input.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import XTextarea from '../../components/BaseComponents/x-textarea.vue';
    import XCheckbox from '../../components/BaseComponents/x-checkbox.vue';
    import InformationTab from '../../components/dashboard/ViewRelatedComponents/InformationTab.vue';
    import SearchAttributes from '../../components/dashboard/ViewRelatedComponents/SearchAttributes.vue';
    import SubmitCancelRemove from '../../components/SubmitCancelRemove.vue';
    import Relations from '../../components/dashboard/themeLayer/Relations.vue';

    const tableFieldsTemplate = {
        alias: '',
        order: 0,
        dataType: '',
        displayInDetail: false,
        displayInTable: false,
        exportable: false,
        popup: false,
        hyperlink: false,
        hyperlinkExpression: '',
        hyperlinkImage: false,
        hyperlinkText: '',
        openExtern: false,
        print: false,
        sort: null
    };

    export default {
        mixins: [modalMixin],
        components: {
            'x-input': XInput,
            'x-select': XSelect,
            'x-textarea': XTextarea,
            'x-checkbox': XCheckbox,
            'information-tab': InformationTab,
            'search-attributes': SearchAttributes,
            'table-relations': Relations,
            SubmitCancelRemove
        },
        props: { id: { type: Number, required: false, default: -1 } },
        data() {
            return {
                RELATIONSHIP_TYPES: RELATIONSHIP_TYPES,
                mode: 0,
                title: '',
                isSaving: false,
                isDeleting: false,
                isLoading: false,
                invalidNameErrorMessage: '',
                layerOptions: [],
                tableResults: [],
                tableResult: {
                    name: '',
                    title: '',
                    description: '',
                    tableName: null,
                    tableSearchResultFields: []
                },
                resultLayer: {},
                autoOpenAllRelationsTabs: false
            };
        },
        computed: {
            isEditMode() {
                return this.mode === MODE_EDIT;
            },
            isBtnSaveDisabled() {
                return !this.validName || this.isSaving || this.isDeleting;
            },
            validName() {
                return this.invalidNameErrorMessage.length === 0;
            },
            hasInformationAttributes() {
                return (
                    this.tableResult.tableName &&
                    this.resultLayer.informationAttributes &&
                    this.resultLayer.informationAttributes.length > 0
                );
            }
        },
        async created() {
            this.isLoading = true;
            await this.$_getAllTableResults();
            await this.$_getServicesLayers();
            if (this.id > 0) {
                this.mode = MODE_EDIT;
                this.title = this.$t('tableResults.edit');
                await this.$_getTableResult();
            } else {
                this.mode = MODE_ADD;
                this.title = this.$t('tableResults.add');
            }
            this.isLoading = false;
        },
        methods: {
            $_onInputName() {
                // auto fill title
                this.tableResult.title = capitalizeValue(this.tableResult.name);
                // check if name already exists
                this.invalidNameErrorMessage = '';
                const found = this.tableResults.find((item) => {
                    return (
                        item.id !== this.id &&
                        item.name.toLowerCase() ===
                            this.tableResult.name.toLowerCase()
                    );
                });
                if (found) {
                    this.invalidNameErrorMessage = this.$t(
                        'tableResults.nameInUse'
                    );
                }
            },
            async $_getInformationAttributes(tableResult) {
                // get current attributes
                const currentAttributes = tableResult.tableSearchResultFields
                    .map((attribute) => ({
                        attribute: attribute.columnName,
                        ...attribute
                    }))
                    .sort(numberSortFunction('order'));

                // get new attributes
                const newAttributes = await this.$_getTableAttributes(
                    tableResult.tableName
                );

                return this.$_getSynchronizedInformationAttributes(
                    currentAttributes,
                    newAttributes
                );
            },
            $_getSynchronizedInformationAttributes(
                currentAttributes,
                newAttributes
            ) {
                // comparer to determine if two columnNames are equal
                const isSameColumnName = (a, b) => a.columnName === b.columnName;

                const onlyInCurrent = onlyInLeftArray(
                    currentAttributes,
                    newAttributes,
                    isSameColumnName
                );
                const onlyInNew = onlyInLeftArray(
                    newAttributes,
                    currentAttributes,
                    isSameColumnName
                );

                // remove obsolete attributes
                const informationAttributes = currentAttributes.filter(
                    (el) => !onlyInCurrent.includes(el)
                );

                // add new attributes
                informationAttributes.push(...onlyInNew);

                // show toast if there are differences
                if (onlyInCurrent.length > 0 || onlyInNew.length > 0) {
                    const data = {
                        message: this.$t('saveTableResult'),
                        variant: 'info',
                        autoHideDelay: 8000
                    };
                    this.showToast(data);
                }

                // return synchronized attributes
                return informationAttributes;
            },
            async $_onTableSelected(value) {
                this.tableResult.tableName = value;
                if (value) {
                    const informationAttributes = await this.$_getTableAttributes(
                        value
                    );
                    this.$_setResultLayer(informationAttributes);
                }
            },
            async $_getTableAttributes(tableName) {
                const result = await this.$store.dispatch('useApiData', {
                    name: 'GetTableAttributes',
                    parameters: { TableName: tableName }
                });
                return result.data.map((attribute) => ({
                    ...tableFieldsTemplate,
                    attribute: attribute.name,
                    columnName: attribute.name,
                    dataType: attribute.binding
                        .replace('java.lang.', '')
                        .toLowerCase(),
                    alias: valueToName(attribute.name)
                }));
            },
            $_onIsPubliclyAccessible(value) {
                this.tableResult.isPubliclyAccessible = value;
            },
            async $_getTableResult() {
                try {
                    const tableResult = await this.$store.dispatch('useApiData', {
                        name: 'GetTableSearchResult',
                        parameters: { Id: this.id }
                    });
                    this.tableResult = Object.assign({}, tableResult?.data);
                    const informationAttributes =
                        await this.$_getInformationAttributes(this.tableResult);
                    this.$_setResultLayer(informationAttributes);
                } catch (error) {
                    const data = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('tableResults.errorGet'),
                        autoHideDelay: 5000
                    };
                    this.isLoading = false;
                    this.showToast(data);
                }
            },
            $_setResultLayer(informationAttributes) {
                this.resultLayer = {
                    exportable: true,
                    print: false,
                    harvest: false,
                    informationAttributes
                };
            },
            async $_getServicesLayers() {
                const tableNames = await this.$store.dispatch('getTableNames');
                this.layerOptions = convertToValueTextPair(
                    tableNames,
                    'layerName',
                    'layerName'
                );
            },
            async $_getAllTableResults() {
                const tableResults = await this.$store.dispatch('useApiData', {
                    name: 'GetTableSearchResults',
                    parameters: {},
                    useLoader: true
                });
                this.tableResults = tableResults.data;
            },
            $_gotoOverview() {
                this.$router.push('/dashboard/tableresults');
            },
            $_onCancel() {
                this.$_gotoOverview();
            },
            async $_onRemove() {
                const confirm = await this.msgBoxConfirm({
                    message: this.$t('tableResults.deleteConfirmation')
                });
                if (confirm) {
                    try {
                        await this.$store.dispatch('useApiData', {
                            name: 'DeleteTableSearchResult',
                            parameters: { Id: this.id }
                        });
                        const data = {
                            message: this.$t('tableResults.removed'),
                            variant: 'success',
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.$_gotoOverview();
                    } catch (error) {
                        const data = {
                            message: this.$t('deleteFailureMessage', [
                                `${error.message}`
                            ]),
                            variant: 'danger',
                            title: this.$t('deleteFailureTitle'),
                            autoHideDelay: 5000
                        };
                        this.showToast(data);
                        this.isDeleting = false;
                    } finally {
                        this.isDeleting = false;
                    }
                }
            },
            $_onSubmit() {
                if (
                    this.$refs.tableResultsForm.checkValidity() &&
                    this.validName &&
                    this.$_canSaveSearchAttributes()
                ) {
                    this.$_onSave();
                }
            },
            async $_onSave() {
                this.isSaving = true;
                const action = this.isEditMode ? 'Update' : 'Create';
                const name = `${action}TableSearchResult`;

                // set order of attributes
                const tableSearchResultFields =
                    this.resultLayer.informationAttributes.map((field, index) => {
                        return {
                            ...field,
                            order: index
                        };
                    });

                const body = {
                    ...this.tableResult,
                    tableSearchResultFields
                };
                try {
                    await this.$store.dispatch('useApiData', {
                        name,
                        useLoader: true,
                        data: body
                    });

                    if (this.isEditMode) {
                        await this.$refs.searchAttributesEdit.saveSearchAttributes();
                        this.$refs.searchAttributesEdit.loadSearchAttributes();
                        await this.$refs.relationsTableEdit.saveTableTableRelations();
                        this.$refs.relationsTableEdit.loadTableTableRelations();
                    }

                    this.isSaving = false;
                    const data = {
                        message: this.$t('tableResults.saved'),
                        variant: 'success',
                        autoHideDelay: 5000
                    };
                    this.showToast(data);
                    if (this.mode === MODE_ADD) {
                        this.$_gotoOverview();
                    }
                } catch (error) {
                    const messageData = {
                        message: error.message,
                        variant: 'danger',
                        title: this.$t('saveFailureTitle'),
                        autoHideDelay: 5000
                    };
                    this.showToast(messageData);
                    this.isSaving = false;
                } finally {
                    this.isSaving = false;
                }
            },
            $_canSaveSearchAttributes() {
                return this.isEditMode &&
                    !this.$refs.searchAttributesEdit.validateSearchAttributes()
                    ? false
                    : true;
            },
            $_updateInformationAttributes(newVal) {
                this.resultLayer.informationAttributes = newVal;
            }
        }
    };
</script>

<style lang="scss">
.hide-tabs-table-result {
    & .nav.nav-tabs {
        display: none;
    }
}
</style>
